import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { CheckCircle2, XCircle, ArrowLeft, Globe } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || 'https://tenant-portal-api-stage.azurewebsites.net'

type Language = 'fi' | 'en'

const translations = {
  fi: {
    successTitle: 'Suostumus annettu onnistuneesti!',
    successDesc: 'Organisaationne käyttäjät voivat nyt kirjautua Tilaajakanavaan Microsoft-tunnuksillaan.',
    nextSteps: 'Seuraavat vaiheet:',
    step1: 'Taksi Helsinki lisää teille käyttäjät järjestelmään',
    step2: 'Käyttäjät voivat kirjautua osoitteessa tilaajakanava.taksihelsinki.fi',
    step3: 'Voit halutessasi rajoittaa pääsyä Azure-portaalissa',
    
    errorTitle: 'Suostumuksen antaminen epäonnistui',
    errorDesc: 'Jokin meni pieleen suostumuksen antamisessa.',
    tryAgain: 'Yritä uudelleen',
    
    backToOnboarding: 'Takaisin',
    contactUs: 'Ota yhteyttä: laskutus@taksihelsinki.fi',
    
    loading: 'Käsitellään...',
    organization: 'Organisaatio',
  },
  en: {
    successTitle: 'Consent granted successfully!',
    successDesc: 'Your organization\'s users can now sign in to Tilaajakanava with their Microsoft credentials.',
    nextSteps: 'Next steps:',
    step1: 'Taksi Helsinki will add users to the system',
    step2: 'Users can sign in at tilaajakanava.taksihelsinki.fi',
    step3: 'You can optionally restrict access in Azure portal',
    
    errorTitle: 'Consent failed',
    errorDesc: 'Something went wrong while granting consent.',
    tryAgain: 'Try again',
    
    backToOnboarding: 'Back',
    contactUs: 'Contact us: laskutus@taksihelsinki.fi',
    
    loading: 'Processing...',
    organization: 'Organization',
  },
}

export default function OnboardingCallbackPage() {
  const [searchParams] = useSearchParams()
  const [lang, setLang] = useState<Language>('fi')
  const [loading, setLoading] = useState(true)
  const [success, setSuccess] = useState(false)
  const [orgName, setOrgName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const t = translations[lang]

  useEffect(() => {
    const processCallback = async () => {
      const tenant = searchParams.get('tenant')
      const adminConsent = searchParams.get('admin_consent')
      const errorCode = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      if (errorCode) {
        setError(errorDescription || errorCode)
        setLoading(false)
        return
      }

      if (!tenant || adminConsent?.toLowerCase() !== 'true') {
        setError('Consent was not granted')
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${API_URL}/onboarding/consent-callback?tenant=${tenant}&admin_consent=${adminConsent}`
        )
        
        if (response.ok) {
          const data = await response.json()
          setSuccess(true)
          setOrgName(data.organization_name)
        } else {
          const data = await response.json()
          setError(data.detail?.description || data.detail?.code || 'Unknown error')
        }
      } catch (err) {
        setError('Connection error')
      } finally {
        setLoading(false)
      }
    }

    processCallback()
  }, [searchParams])

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      {/* Language switcher */}
      <div className="absolute top-4 right-4 flex gap-2">
        <button
          onClick={() => setLang('fi')}
          className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm ${
            lang === 'fi' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          <Globe className="w-4 h-4" />
          Suomi
        </button>
        <button
          onClick={() => setLang('en')}
          className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm ${
            lang === 'en' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          <Globe className="w-4 h-4" />
          English
        </button>
      </div>

      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <img 
              src="/images/logo-th.webp" 
              alt="Taksi Helsinki" 
              className="h-12 mx-auto"
            />
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-gray-200 border-t-black rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">{t.loading}</p>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t.successTitle}</h1>
              {orgName && (
                <p className="text-lg text-green-600 font-medium mb-4">
                  {t.organization}: {orgName}
                </p>
              )}
              <p className="text-gray-600 mb-6">{t.successDesc}</p>
              
              <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                <h3 className="font-medium text-gray-900 mb-2">{t.nextSteps}</h3>
                <ol className="text-sm text-gray-600 space-y-2">
                  <li className="flex gap-2">
                    <span className="text-green-500 font-bold">1.</span>
                    {t.step1}
                  </li>
                  <li className="flex gap-2">
                    <span className="text-green-500 font-bold">2.</span>
                    {t.step2}
                  </li>
                  <li className="flex gap-2">
                    <span className="text-green-500 font-bold">3.</span>
                    {t.step3}
                  </li>
                </ol>
              </div>

              <p className="text-sm text-gray-500">{t.contactUs}</p>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t.errorTitle}</h1>
              <p className="text-gray-600 mb-2">{t.errorDesc}</p>
              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3 mb-6">
                  {error}
                </p>
              )}
              
              <Link
                to="/onboarding"
                className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800"
              >
                <ArrowLeft className="w-4 h-4" />
                {t.tryAgain}
              </Link>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Taksi Helsinki Oy
        </p>
      </div>
    </div>
  )
}
